import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useConfirmationModal, useSnackbars } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { getApiClientErrorDetailFromError } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { getSchemaUpgradeAlerts, LATEST_SCHEMA_VERSION, type WarehouseSync } from '@/services/db/warehouse-sync/entity';
import { useDeleteWarehouseSync, useUpdateWarehouseSync } from '@/services/db/warehouse-sync/mutations';
import { getWarehouseSyncCache, setWarehouseSyncCache } from '@/services/db/warehouse-sync/cache';
import type { BucketConfigDraft, WarehouseDataOptionID } from '@/features/warehouse-sync';
import { WAREHOUSE_DESTINATION_CONFIG_KEYS, type WarehouseDestination } from '@/services/db/warehouse-sync/entity';
import type { SchemaUpgradeAlertState } from './components/details';
import type { SchemaUpgradeSettings } from './components/update-schema-version';

const NO_ALERTS: SchemaUpgradeAlertState = { people: false, outputs: false, subjects: false };

/** Stable ids so a run of saves replaces one toast rather than stacking them. */
const SNACKBAR_IDS = {
  settings: 'warehouse-sync.update',
  schema: 'warehouse-sync.schema-update',
  bucketConfig: 'warehouse.bucket-config',
} as const;

type UseConfigActionsArgs = {
  /** The resolved workspace, not the URL segment — see the reads in `routing.tsx`. */
  workspaceId: string;
  sync: WarehouseSync;
  destination: WarehouseDestination;
  destinationName: string;
  /** The confirmation body, built by the page so the copy stays with the markup. */
  disconnectMessage: ReactNode;
};

/**
 * The writes the config view performs — toggling an export, upgrading the schema,
 * rotating the bucket credentials, disconnecting — plus the state their side
 * effects leave behind.
 */
export function useConfigActions({
  workspaceId,
  sync,
  destination,
  destinationName,
  disconnectMessage,
}: UseConfigActionsArgs) {
  const snackbars = useSnackbars();
  const confirm = useConfirmationModal();
  const { track } = useAnalytics();
  const queryClient = useQueryClient();

  // Keyed on the resolved workspace: `requireWorkspaceId` parses the URL, which can name a
  // workspace the user cannot access — the reads would then show one workspace while the writes
  // targeted another.
  const updateSync = useUpdateWarehouseSync({ workspaceId: Number(workspaceId) });
  const deleteSync = useDeleteWarehouseSync({ workspaceId: Number(workspaceId) });

  /**
   * The server's own detail when it sent one. It is not decoration: re-enabling
   * `people` after a week off is refused with the only instruction that clears the
   * dead end ("contact win@customer.io"), and a generic line strands the user
   * retrying a write that can never succeed.
   */
  const reportFailure = (error: unknown, fallback: string) => {
    const detail = getApiClientErrorDetailFromError(error);
    snackbars.create({ message: isEmptyString(detail) ? fallback : detail, variant: 'critical' });
  };

  // Which exports the upgrade turned off, so the affected rows can explain themselves.
  // Scoped to this visit: leaving the page clears them.
  const [upgradeAlerts, setUpgradeAlerts] = useState<SchemaUpgradeAlertState>(NO_ALERTS);
  const [isSchemaModalOpen, setIsSchemaModalOpen] = useState(false);

  const toggleOption = (optionId: WarehouseDataOptionID, nextValue: boolean) => {
    // Moved before the write, and put back if it fails. A switch that stays put
    // until the round trip lands reads as a dead control, and the row is disabled
    // meanwhile, so there is nothing else to show that the click registered.
    const previous = getWarehouseSyncCache(queryClient, Number(workspaceId));
    setWarehouseSyncCache(queryClient, Number(workspaceId), { ...sync, [optionId]: nextValue });

    updateSync.mutate(
      { syncId: sync.id, sync: { update_type: 'settings', [optionId]: nextValue } },
      {
        onSuccess: () => {
          snackbars.create({
            id: SNACKBAR_IDS.settings,
            message: 'Updated your warehouse sync settings',
            variant: 'default',
          });
          void track('warehouse', nextValue ? 'exportEnabled' : 'exportDisabled', {
            Type: destinationName,
            // The analytics key, not a label — renaming it would break the dashboards.
            'Export Type': optionId,
          });
        },
        onError: (error) => {
          if (previous !== undefined) {
            setWarehouseSyncCache(queryClient, Number(workspaceId), previous);
          }
          // Without this the toggle silently snaps back and the user retries in a loop.
          reportFailure(error, 'We could not update your warehouse sync settings. Try again.');
        },
      },
    );
  };

  const upgradeSchema = (settings: SchemaUpgradeSettings) => {
    // Captured before the write: the response carries the already-migrated
    // record, which would read as "nothing was disabled".
    const alerts = getSchemaUpgradeAlerts(sync.version, {
      people: sync.people,
      outputs: sync.outputs,
      subjects: sync.subjects,
    });

    updateSync.mutate(
      { syncId: sync.id, sync: { update_type: 'schema_version', version: LATEST_SCHEMA_VERSION, ...settings } },
      {
        onSuccess: () => {
          setUpgradeAlerts(alerts);
          setIsSchemaModalOpen(false);
          snackbars.create({
            id: SNACKBAR_IDS.schema,
            message: 'Schema version for your sync updated',
            variant: 'default',
          });
        },
        onError: (error) => reportFailure(error, 'We could not upgrade your sync schema. Try again.'),
      },
    );
  };

  /** Reported when the configuration modal opens, to pair with the completion below. */
  const startBucketConfigUpdate = () => {
    void track('warehouse', 'updateConfigurationStarted', { Type: destinationName });
  };

  /**
   * Rotate the bucket credentials.
   *
   * Only `update_type` and the one storage block are sent. The generated request
   * type also admits `attributes`/`contents`/`outputs`/`subjects` on this variant
   * — the app being replaced serialised the whole record, so they always went
   * along — but the server's `*_config` branch swaps `cfg.Storage` and reads
   * nothing else, so omitting them cannot disturb the export toggles.
   */
  const updateBucketConfig = async (config: BucketConfigDraft): Promise<boolean> => {
    const configKey = WAREHOUSE_DESTINATION_CONFIG_KEYS[destination];
    try {
      await updateSync.mutateAsync({
        syncId: sync.id,
        sync: { update_type: configKey, [configKey]: config },
      });
      snackbars.create({
        id: SNACKBAR_IDS.bucketConfig,
        message: `Updated ${destinationName} bucket configuration`,
        variant: 'default',
      });
      void track('warehouse', 'updateConfigurationCompleted', { Type: destinationName });
      return true;
    } catch (error) {
      reportFailure(error, `We could not update your ${destinationName} bucket configuration. Try again.`);
      return false;
    }
  };

  const disconnect = () => {
    confirm({
      title: `Disconnect sync to ${destinationName}`,
      message: disconnectMessage,
      isCritical: true,
      confirmLabel: 'Disconnect sync',
      // Awaited by the modal, which is what disables its buttons while the delete is in flight.
      onConfirm: async () => {
        try {
          await deleteSync.mutateAsync({ syncId: sync.id });
          // Reported only on a successful disconnect.
          void track('warehouse', 'disconnectSyncCompleted', { Type: destinationName });
        } catch (error) {
          reportFailure(error, `We could not disconnect your sync to ${destinationName}. Try again.`);
        }
      },
    });
  };

  // A single `useUpdateWarehouseSync` backs three unrelated controls, so a bare
  // `isPending` would grey out the export toggles while the bucket config saves.
  // The in-flight `update_type` says which control actually asked for the write.
  const pendingUpdateType = updateSync.isPending ? updateSync.variables?.sync.update_type : undefined;

  return {
    upgradeAlerts,
    isSchemaModalOpen,
    setIsSchemaModalOpen,
    toggleOption,
    upgradeSchema,
    startBucketConfigUpdate,
    updateBucketConfig,
    disconnect,
    isTogglingOption: pendingUpdateType === 'settings',
    isUpgradingSchema: pendingUpdateType === 'schema_version',
    isSavingBucketConfig: pendingUpdateType === WAREHOUSE_DESTINATION_CONFIG_KEYS[destination],
    isDeleting: deleteSync.isPending,
  };
}
